export const pikbaseFaqClasses = {
    faqPanel: "mx-auto max-w-3xl",
    faqHeader: "pb-6",
    faqTitle: "text-2xl font-semibold text-foreground sm:text-3xl",
    faqFilters: "flex flex-wrap gap-2 pb-6 [&_button]:min-h-11 [&_button]:rounded-md [&_button]:border [&_button]:border-border [&_button]:bg-background [&_button]:px-4 [&_button]:py-2 [&_button]:text-sm [&_button]:font-medium [&_button:hover]:bg-muted [&_button:focus-visible]:outline [&_button:focus-visible]:outline-2 [&_button:focus-visible]:outline-primary [&_button[aria-pressed=true]]:border-primary [&_button[aria-pressed=true]]:bg-primary [&_button[aria-pressed=true]]:text-primary-foreground",
    faqItem: "border-t border-border/70 py-2",
    faqQuestion: "flex min-h-14 w-full items-center justify-between gap-4 rounded-md px-2 py-3 text-left text-base font-medium text-foreground hover:bg-muted/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary [&_span:first-child]:min-w-0 [&_span:last-child]:shrink-0 [&_span:last-child]:text-xl [&_span:last-child]:text-muted-foreground",
    faqAnswer: "px-2 pb-4 pt-1 text-sm leading-7 text-muted-foreground",
};
const fallbackPolicy = (title, lastUpdated) => ({
    title,
    lastUpdated,
    markdown: `# ${title}\n\nThe full policy is loaded from the selected language Markdown document.`,
});
export const pikbaseLegalConfig = {
    policyVersion: "2026-08-27",
    consentStorageKey: "pikbase-cookie-consent",
    consentDelayMs: 750,
    paths: {
        terms: "/terms",
        privacy: "/privacy",
        refunds: "/refunds",
        cookies: "/cookies",
        faq: "/faq",
    },
    policies: {
        terms: fallbackPolicy("Pikbase Terms of Service", "August 27, 2026"),
        privacy: fallbackPolicy("Pikbase Privacy Policy", "August 16, 2026"),
        refunds: fallbackPolicy("Pikbase Refund and Cancellation Policy", "September 12, 2026"),
        cookies: fallbackPolicy("Pikbase Cookie Policy", "August 16, 2026"),
    },
    faq: {
        title: "Pikbase FAQ",
        subtitle: "Answers about the platform and account controls.",
        allCategoriesLabel: "All",
        items: [
            {
                id: "account-purpose",
                category: "Account",
                question: "What is a Pikbase Account?",
                answer: "It is the surface where you sign in, provision workspaces, manage the Pikbase Team that shares billing and account access, and change your security and privacy settings. Product work itself happens inside the workspaces you provision from it.",
            },
            {
                id: "after-verification",
                category: "Account",
                question: "What happens after I verify my email address?",
                answer: "Verification activates the account. You can then sign in, provision a workspace, and invite the people who need access to it.",
            },
            {
                id: "team-vs-workspace",
                category: "Workspaces",
                question: "What is the difference between the Pikbase Team and a workspace's members?",
                answer: "The Pikbase Team is the account-level group that shares billing and account access. Each workspace keeps its own separate membership and permissions, managed from that workspace.",
            },
            {
                id: "invite-member",
                category: "Workspaces",
                question: "How do I invite someone?",
                answer: "Open Account, then Team, and send an invitation with a name, a work email address, and a role. The invitation expires after seven days and can be resent or revoked while it is still pending.",
            },
            {
                id: "billing",
                category: "Billing",
                question: "Where do I manage billing?",
                answer: "Open Account, then Plan & subscriptions, to review your subscriptions and payment history. Account administrators can purchase workspaces from Workspaces and add capacity through Marketplace. Use the available subscription controls or payment-provider portal to manage an existing subscription; contact your account administrator if you do not have access.",
            },
            {
                id: "data-controls",
                category: "Data",
                question: "Can I remove the data held for my account?",
                answer: "Account, then Settings, then Data lets you export your profile. Contact support for account closure and data-removal requests. Account and workspace closure are subject to retention requirements.",
            },
            {
                id: "security",
                category: "Security",
                question: "How do I change the password used to sign in?",
                answer: "Open Account, then Settings, then Security. Changing a password requires the current one, and a new password must be at least twelve characters long.",
            },
            {
                id: "support",
                category: "Support",
                question: "Where do I get help?",
                answer: "Product documentation is published at docs.pikbase.com and service availability at status.pikbase.com. For anything specific to your account, contact the person who owns your subscription.",
            },
        ],
    },
    consent: {
        title: "Your privacy choices",
        description: "Pikbase uses essential storage to operate the service. You can choose whether optional categories are enabled.",
        acceptLabel: "Accept optional",
        rejectLabel: "Essential only",
        closeLabel: "Close",
        categories: ["essential", "functional", "analytics", "marketing"],
        requiredPolicies: [
            {
                policyId: "privacy",
                href: "/privacy",
                label: "Privacy Policy",
            },
            {
                policyId: "cookies",
                href: "/cookies",
                label: "Cookie Policy",
            },
        ],
    },
    externalLegalApproval: {
        status: "pending",
        provider: "Pikbase legal review",
    },
};
export function pikbaseLegalConfigForLocale(locale) {
    if (locale.toLowerCase().split("-")[0] !== "tr")
        return pikbaseLegalConfig;
    const categories = {
        Account: "Hesap",
        Workspaces: "Çalışma alanları",
        Billing: "Faturalandırma",
        Data: "Veriler",
        Security: "Güvenlik",
        Support: "Destek",
    };
    const translations = {
        "account-purpose": {
            question: "Pikbase Hesabı nedir?",
            answer: "Oturum açtığınız, çalışma alanları oluşturduğunuz, faturalandırma ve hesap erişimini paylaşan Pikbase Ekibini yönettiğiniz, güvenlik ve gizlilik ayarlarınızı değiştirdiğiniz arayüzdür. Ürünle ilgili asıl çalışmalar, bu arayüzden oluşturduğunuz çalışma alanlarında yapılır.",
        },
        "after-verification": {
            question: "E-posta adresimi doğruladıktan sonra ne olur?",
            answer: "Doğrulama, hesabı etkinleştirir. Ardından oturum açabilir, bir çalışma alanı oluşturabilir ve bu alana erişmesi gereken kişileri davet edebilirsiniz.",
        },
        "team-vs-workspace": {
            question: "Pikbase Ekibi ile bir çalışma alanının üyeleri arasındaki fark nedir?",
            answer: "Pikbase Ekibi, faturalandırma ve hesap erişimini paylaşan hesap düzeyindeki gruptur. Her çalışma alanının, o çalışma alanından yönetilen ayrı üyelikleri ve izinleri vardır.",
        },
        "invite-member": {
            question: "Birini nasıl davet ederim?",
            answer: "Hesap bölümünü, ardından Ekip bölümünü açın ve bir ad, iş e-posta adresi ve rol belirterek davet gönderin. Davetin geçerliliği yedi gün sonra sona erer; davet henüz beklemedeyken yeniden gönderilebilir veya iptal edilebilir.",
        },
        billing: {
            question: "Faturalandırmayı nereden yönetirim?",
            answer: "Aboneliklerinizi ve ödeme geçmişinizi incelemek için Hesap bölümünü, ardından Plan ve abonelikler bölümünü açın. Hesap yöneticileri, Çalışma alanları bölümünden çalışma alanı satın alabilir ve Marketplace üzerinden kapasite ekleyebilir. Mevcut bir aboneliği yönetmek için sunulan abonelik kontrollerini veya ödeme sağlayıcısının portalını kullanın; erişiminiz yoksa hesap yöneticinizle iletişime geçin.",
        },
        "data-controls": {
            question: "Hesabım için tutulan verileri kaldırabilir miyim?",
            answer: "Hesap, ardından Ayarlar, ardından Veriler bölümünden profilinizi dışa aktarabilirsiniz. Hesabın kapatılması ve verilerin kaldırılması talepleri için destek ekibiyle iletişime geçin. Hesap ve çalışma alanının kapatılması, saklama gerekliliklerine tabidir.",
        },
        security: {
            question: "Oturum açmak için kullandığım parolayı nasıl değiştiririm?",
            answer: "Hesap bölümünü, ardından Ayarlar, ardından Güvenlik bölümünü açın. Parolayı değiştirmek için mevcut parola gerekir ve yeni parola en az on iki karakter uzunluğunda olmalıdır.",
        },
        support: {
            question: "Nereden yardım alabilirim?",
            answer: "Ürün belgeleri docs.pikbase.com adresinde, hizmetin kullanılabilirlik durumu ise status.pikbase.com adresinde yayımlanır. Hesabınıza özel konular için aboneliğinizin sahibi olan kişiyle iletişime geçin.",
        },
    };
    return {
        ...pikbaseLegalConfig,
        policies: {
            terms: {
                ...pikbaseLegalConfig.policies.terms,
                title: "Pikbase Hizmet Şartları",
            },
            privacy: {
                ...pikbaseLegalConfig.policies.privacy,
                title: "Pikbase Gizlilik Politikası",
            },
            refunds: {
                ...pikbaseLegalConfig.policies.refunds,
                title: "Pikbase İade ve İptal Politikası",
            },
            cookies: {
                ...pikbaseLegalConfig.policies.cookies,
                title: "Pikbase Çerez Politikası",
            },
        },
        faq: {
            ...pikbaseLegalConfig.faq,
            title: "Pikbase Sık Sorulan Sorular",
            subtitle: "Platform ve hesap kontrolleri hakkında yanıtlar.",
            allCategoriesLabel: "Tümü",
            items: pikbaseLegalConfig.faq.items.map((item) => ({
                ...item,
                ...translations[item.id],
                category: categories[item.category] ?? item.category,
            })),
        },
        consent: {
            ...pikbaseLegalConfig.consent,
            title: "Gizlilik tercihleriniz",
            description: "Pikbase, hizmeti çalıştırmak için zorunlu depolama kullanır. İsteğe bağlı kategorilerin etkinleştirilip etkinleştirilmeyeceğini seçebilirsiniz.",
            acceptLabel: "İsteğe bağlı olanları kabul et",
            rejectLabel: "Yalnızca zorunlu olanlar",
            closeLabel: "Kapat",
            requiredPolicies: pikbaseLegalConfig.consent.requiredPolicies.map((policy) => ({
                ...policy,
                label: policy.policyId === "privacy"
                    ? "Gizlilik Politikası"
                    : "Çerez Politikası",
            })),
        },
        shell: {
            eyebrow: "Pikbase yasal bilgiler",
            lastUpdatedLabel: "Son güncelleme",
            navigationLabel: "Politika gezinmesi",
            readPolicyLabel: "Politikayı oku",
            contactLabel: "İletişim",
            backToTopLabel: "Başa dön",
            loadingLabel: "Politika yükleniyor",
            errorLabel: "Politika içeriği kullanılamıyor.",
            emptyLabel: "Henüz soru bulunmuyor.",
            customizeLabel: "Özelleştir",
            savePreferencesLabel: "Tercihleri kaydet",
            settingsLabel: "Çerez ayarları",
            preferencesTitle: "Çerez tercihleri",
            preferencesDescription: "Zorunlu çerezler her zaman etkindir. İsteğe bağlı kategoriler istediğiniz zaman değiştirilebilir.",
            categoriesLabel: "İsteğe bağlı çerez kategorileri",
            cancelLabel: "İptal",
            categoryLabels: {
                essential: "Zorunlu",
                functional: "İşlevsel",
                analytics: "Analitik",
                marketing: "Pazarlama",
            },
        },
    };
}
export function pikbaseLegalTitleForLocale(path, locale) {
    if (locale.toLowerCase().split("-")[0] !== "tr")
        return undefined;
    const config = pikbaseLegalConfigForLocale(locale);
    if (path === config.paths.faq)
        return config.faq.title;
    if (path === config.paths.terms)
        return config.policies.terms.title;
    if (path === config.paths.privacy)
        return config.policies.privacy.title;
    if (path === config.paths.refunds)
        return config.policies.refunds.title;
    if (path === config.paths.cookies)
        return config.policies.cookies.title;
    return undefined;
}
//# sourceMappingURL=pikbase.js.map